import { useState } from 'react'
import { Plus, Users, Trash2, Link2, ArrowLeft } from 'lucide-react'
import { usePlayers, useDeletePlayer, useLinkPlayer } from '../hooks/usePlayers'
import { useAuth } from '../contexts/AuthContext'
import AddPlayerModal from './AddPlayerModal'
import ConfirmationModal from './ConfirmationModal'
import Spinner from './Spinner'
import { getInitials } from '../utils/colors'
import { hasHandicapIndex, formatHandicapIndex } from '../utils/handicaps'

export default function Players({ onBack }) {
  const { user } = useAuth()
  const [showAddPlayer, setShowAddPlayer] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const { data: players, isLoading } = usePlayers()
  const deletePlayer = useDeletePlayer()
  const linkPlayer = useLinkPlayer()

  const handleDelete = () => {
    if (!deleteTarget) return
    deletePlayer.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    })
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--bg-tertiary)',
                flexShrink: 0,
              }}
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Players</h1>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowAddPlayer(true)}
          style={{ padding: '0 1rem', height: '40px', fontSize: '0.8125rem' }}
        >
          <Plus size={18} />
          Add
        </button>
      </div>

      {isLoading ? (
        <Spinner message="Loading players..." />
      ) : !players?.length ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
          gap: '1rem',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}>
          <Users size={48} />
          <p style={{ fontSize: '0.875rem' }}>Add your golf buddies to get started</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {players.map((player) => (
            <div
              key={player.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.875rem 1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: player.avatar_color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '0.875rem',
                color: '#fff',
                flexShrink: 0,
              }}>
                {getInitials(player)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span style={{ fontWeight: '600', fontSize: '0.9375rem' }}>
                    {player.display_name}
                  </span>
                  {player.user_id && (
                    <span style={{
                      fontSize: '0.5625rem',
                      fontWeight: '600',
                      color: 'var(--accent-primary)',
                      backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.15)',
                      padding: '0.0625rem 0.375rem',
                      borderRadius: '1rem',
                      textTransform: 'uppercase',
                    }}>
                      Linked
                    </span>
                  )}
                </div>
                {player.email && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.125rem' }}>
                    {player.email}
                  </div>
                )}
                {hasHandicapIndex(player.handicap_index) && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: '0.125rem' }}>
                    HCP Index: {formatHandicapIndex(player.handicap_index)}
                  </div>
                )}
              </div>
              {/* Link to my account button — show if player has email matching user but no user_id */}
              {!player.user_id && player.email && user?.email &&
                player.email.toLowerCase() === user.email.toLowerCase() && (
                <button
                  onClick={() => linkPlayer.mutate(player.id)}
                  disabled={linkPlayer.isPending}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '9999px',
                    backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.1)',
                    color: 'var(--accent-primary)',
                    fontSize: '0.6875rem',
                    fontWeight: '600',
                    flexShrink: 0,
                  }}
                >
                  <Link2 size={14} />
                  Link
                </button>
              )}
              <button
                onClick={() => setDeleteTarget(player)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  color: 'var(--text-muted)',
                  borderRadius: '9999px',
                  flexShrink: 0,
                }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddPlayer && (
        <AddPlayerModal onClose={() => setShowAddPlayer(false)} />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete Player"
          message={`Remove ${deleteTarget.display_name} from your roster?`}
          confirmText="Delete"
          isDanger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}
